import { type ActionEvent, Controller } from "@hotwired/stimulus"

import { debounceAsync } from "@/helpers/utils"

export default class extends Controller {
  static targets = ["input", "results"]
  declare readonly inputTarget: HTMLInputElement
  declare readonly resultsTarget: HTMLElement

  static values = { endpoint: String, listboxId: String }
  declare readonly endpointValue: string
  declare readonly listboxIdValue: string

  initialize(): void {
    this.lookup = debounceAsync(this.lookup.bind(this), 250)
  }

  resultsTargetConnected(): void {
    // A user could type in the input and click away before results frame returned, so hide it
    // if element is not focused.
    this.resultsTarget.hidden = !this.hasFocusedElement()
  }

  hideResultsIfBlurred(e: FocusEvent): void {
    // Controller element contains both input and "clear" button, each of which can gain/lose
    // focus. Only hide results if focus lost within controller element itself.
    this.resultsTarget.hidden = !this.hasFocusedElement(e.relatedTarget as Element)
  }

  async lookup({ target }: ActionEvent): Promise<void> {
    const inputValue = (target as HTMLInputElement).value
    const url = new URL(this.endpointValue, window.location.origin)
    const params = new URLSearchParams({
      q: inputValue,
      listbox_id: this.listboxIdValue
    })
    url.search = params.toString()
    const result = await window.fetch(url.toString())
    const html = await result.text()

    window.Turbo.session.renderStreamMessage(html)
    this.showResults()
  }

  selectResult({ params: { name } }: ActionEvent): void {
    this.inputTarget.value = name
    this.resultsTarget.hidden = true
  }

  showResults(): void {
    this.resultsTarget.hidden = false
  }

  private hasFocusedElement(element?: Element | null): boolean {
    return this.element.contains(element || document.activeElement)
  }
}
